import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DOCUMENT_REMINDER_DEFAULTS,
  EMAIL_LOCALES,
  EMAIL_LOCALE_DIRECTIONS,
  EMAIL_LOCALE_LABELS,
  EMAIL_LOG_STATUSES,
  EMAIL_TEMPLATES,
  EMAIL_TEMPLATE_LABELS,
  type DocumentReminderSettings,
  type EmailLocale,
  type EmailLogEntry,
  type EmailLogStatus,
  type EmailRenderResult,
  type EmailSendResult,
  type EmailTemplateDescriptor,
  type EmailTemplateKey,
  type PaginatedResponse,
} from '@driverforce/shared-types';
import { Card } from '@driverforce/shared-ui';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { toastApiError } from '../lib/apiToast';

type TemplatesState =
  | { kind: 'loading' }
  | { kind: 'ready'; templates: EmailTemplateDescriptor[] }
  | { kind: 'error'; message: string }
  | { kind: 'forbidden' };

type PreviewResponse = EmailRenderResult & {
  template_key: EmailTemplateKey;
  locale: EmailLocale;
};

type SendStatus =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; message: string }
  | { kind: 'failed'; message: string };

const PREVIEW_DEBOUNCE_MS = 350;

const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// ── Send history ────────────────────────────────────────────────

type HistoryState =
  | { kind: 'loading' }
  | { kind: 'ready'; page: PaginatedResponse<EmailLogEntry> }
  | { kind: 'error' };

interface HistoryFilters {
  template_key: EmailTemplateKey | '';
  status: EmailLogStatus | '';
  recipient: string;
}

const INITIAL_HISTORY_FILTERS: HistoryFilters = { template_key: '', status: '', recipient: '' };
const HISTORY_PAGE_SIZE = 25;
const RECIPIENT_DEBOUNCE_MS = 350;

const STATUS_CHIP: Record<EmailLogStatus, string> = {
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  skipped: 'bg-gray-100 text-gray-600',
  queued: 'bg-amber-100 text-amber-800',
};

const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const EmailHistory: React.FC<{ token: string | null }> = ({ token }) => {
  const [state, setState] = useState<HistoryState>({ kind: 'loading' });
  const [filters, setFilters] = useState<HistoryFilters>(INITIAL_HISTORY_FILTERS);
  const [recipientInput, setRecipientInput] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  // Debounce the free-text recipient search into the applied filters.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((f) =>
        f.recipient === recipientInput.trim() ? f : { ...f, recipient: recipientInput.trim() },
      );
      setPage(1);
    }, RECIPIENT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [recipientInput]);

  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    setState((s) => (s.kind === 'ready' ? s : { kind: 'loading' }));
    api.setToken(token);
    const params = new URLSearchParams();
    if (filters.template_key) params.set('template_key', filters.template_key);
    if (filters.status) params.set('status', filters.status);
    if (filters.recipient) params.set('recipient', filters.recipient);
    params.set('page', String(page));
    params.set('pageSize', String(HISTORY_PAGE_SIZE));

    const res = await api.get<PaginatedResponse<EmailLogEntry>>(`/emails/logs?${params}`);
    if (myRequestId !== requestIdRef.current) return; // stale
    if (res.success && res.data) {
      setState({ kind: 'ready', page: res.data });
      return;
    }
    toastApiError(res, 'Failed to load email history');
    setState({ kind: 'error' });
  }, [token, filters, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = state.kind === 'ready' ? Math.max(1, state.page.totalPages) : 1;

  return (
    <div>
      {/* Filter bar */}
      <Card padding="md" className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="flex flex-col text-xs font-medium text-gray-600">
            Template
            <select
              className="mt-1 rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
              value={filters.template_key}
              onChange={(e) => {
                setFilters((f) => ({ ...f, template_key: e.target.value as EmailTemplateKey | '' }));
                setPage(1);
              }}
            >
              <option value="">All templates</option>
              {EMAIL_TEMPLATES.map((t) => (
                <option key={t} value={t}>
                  {EMAIL_TEMPLATE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col text-xs font-medium text-gray-600">
            Status
            <select
              className="mt-1 rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
              value={filters.status}
              onChange={(e) => {
                setFilters((f) => ({ ...f, status: e.target.value as EmailLogStatus | '' }));
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {EMAIL_LOG_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col text-xs font-medium text-gray-600">
            Recipient
            <input
              type="search"
              placeholder="Search address…"
              className="mt-1 rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
            />
          </label>

          <div className="flex items-end gap-3 text-sm">
            <button
              type="button"
              onClick={() => {
                setFilters(INITIAL_HISTORY_FILTERS);
                setRecipientInput('');
                setPage(1);
              }}
              className="text-gray-600 hover:text-gray-900 underline"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => void load()}
              className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:border-gray-400"
            >
              Refresh
            </button>
          </div>
        </div>
      </Card>

      {state.kind === 'error' && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex items-center justify-between"
        >
          <span>Could not load email history.</span>
          <button type="button" onClick={() => void load()} className="underline ml-2">
            Retry
          </button>
        </div>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.kind === 'loading' &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} aria-busy="true">
                    <td colSpan={5} className="px-4 py-3">
                      <span className="block h-6 w-full shimmer" />
                    </td>
                  </tr>
                ))}

              {state.kind === 'ready' && state.page.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                    No emails match these filters.
                  </td>
                </tr>
              )}

              {state.kind === 'ready' &&
                state.page.items.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-sm text-gray-500">
                      {formatWhen(r.created_at)}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <div className="text-sm text-gray-800">{EMAIL_TEMPLATE_LABELS[r.template_key] ?? r.template_key}</div>
                      <div className="text-xs text-gray-500 uppercase">{r.locale}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-800 break-all">
                      {r.recipient}
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-500 max-w-xs">
                      <div className="truncate" title={r.subject}>{r.subject}</div>
                      {r.status === 'failed' && r.error_message && (
                        <div className="mt-0.5 text-xs text-red-600">{r.error_message}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_CHIP[r.status]}`}>
                        {r.status}
                      </span>
                      {r.is_test && (
                        <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-100 text-blue-800 align-middle">
                          TEST
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {state.kind === 'ready' && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
          <div>
            {state.page.total === 0
              ? 'No results'
              : `Showing page ${state.page.page} of ${totalPages} · ${state.page.total} total`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Document-upload reminders ───────────────────────────────────
// Settings panel for the automatic `document_reminder` sweep: how long after
// the docs stall the first nudge goes out, how often it repeats, and the
// lifetime cap per user. The API scheduler re-reads these every tick.

type RemindersState =
  | { kind: 'loading' }
  | { kind: 'ready'; settings: DocumentReminderSettings }
  | { kind: 'error' };

type SweepSummary = { enabled: boolean; candidates: number; sent: number; failed: number };

const RemindersPanel: React.FC<{ token: string | null }> = ({ token }) => {
  const [state, setState] = useState<RemindersState>({ kind: 'loading' });
  const [form, setForm] = useState<DocumentReminderSettings>(DOCUMENT_REMINDER_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<SweepSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      api.setToken(token);
      const res = await api.get<DocumentReminderSettings>('/emails/reminder-settings');
      if (cancelled) return;
      if (res.success && res.data) {
        setState({ kind: 'ready', settings: res.data });
        setForm(res.data);
      } else {
        setState({ kind: 'error' });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const dirty =
    state.kind === 'ready' &&
    (form.enabled !== state.settings.enabled ||
      form.first_after_hours !== state.settings.first_after_hours ||
      form.interval_hours !== state.settings.interval_hours ||
      form.max_sends !== state.settings.max_sends);

  const save = useCallback(async () => {
    setSaving(true);
    setSavedAt(null);
    const res = await api.put<DocumentReminderSettings>('/emails/reminder-settings', form);
    setSaving(false);
    if (res.success && res.data) {
      setState({ kind: 'ready', settings: res.data });
      setForm(res.data);
      setSavedAt(Date.now());
    } else {
      toastApiError(res, 'Could not save reminder settings');
    }
  }, [form]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setLastRun(null);
    const res = await api.post<SweepSummary>('/emails/reminders/run');
    setRunning(false);
    if (res.success && res.data) setLastRun(res.data);
    else toastApiError(res, 'Could not run the reminder sweep');
  }, []);

  if (state.kind === 'loading') {
    return <span className="block h-64 w-full max-w-2xl shimmer" aria-busy="true" />;
  }
  if (state.kind === 'error') {
    return (
      <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
        Could not load reminder settings. Reload the page to try again.
      </div>
    );
  }

  const intervalDays = form.interval_hours / 24;

  return (
    <div className="max-w-2xl space-y-4">
      <Card>
        <div className="p-4 sm:p-5 space-y-5">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Document upload reminders</h3>
            <p className="mt-1 text-xs text-gray-500">
              Users who selected documents but haven&apos;t uploaded the files get the
              &ldquo;07 · Document reminder&rdquo; email. The first reminder goes out after the
              first-reminder delay below, then repeats at the repeat interval until the cap is
              reached, the documents are uploaded, or the user unsubscribes via the link in the
              email.
            </p>
          </div>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-900">Send reminders automatically</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">
                First reminder after (hours)
              </span>
              <input
                type="number"
                min={1}
                max={720}
                value={form.first_after_hours}
                onChange={(e) =>
                  setForm((f) => ({ ...f, first_after_hours: Math.trunc(Number(e.target.value)) }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-gray-500">
                Wait after the documents stalled before the first nudge.
              </span>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">
                Repeat interval (hours)
              </span>
              <input
                type="number"
                min={1}
                max={720}
                value={form.interval_hours}
                onChange={(e) =>
                  setForm((f) => ({ ...f, interval_hours: Math.trunc(Number(e.target.value)) }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-gray-500">
                {Number.isFinite(intervalDays) && form.interval_hours >= 24
                  ? `≈ every ${intervalDays % 1 === 0 ? intervalDays : intervalDays.toFixed(1)} day${intervalDays >= 2 ? 's' : ''}. `
                  : ''}
                Spacing between repeat reminders.
              </span>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">
                Maximum reminders per user
              </span>
              <input
                type="number"
                min={0}
                max={20}
                value={form.max_sends}
                onChange={(e) =>
                  setForm((f) => ({ ...f, max_sends: Math.trunc(Number(e.target.value)) }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-gray-500">
                Lifetime cap. 0 stops all sending.
              </span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={!dirty || saving}
              className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
            {savedAt !== null && !dirty && (
              <span className="text-xs text-green-700">Saved — applies from the next sweep.</span>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-4 sm:p-5 space-y-3">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Run a sweep now</h3>
            <p className="mt-1 text-xs text-gray-500">
              The server sweeps on its own every 15 minutes. Run one immediately to verify the
              settings above — due reminders are really sent and appear in History.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void runNow()}
              disabled={running}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {running ? 'Running…' : 'Run sweep now'}
            </button>
            {lastRun && (
              <span className="text-xs text-gray-600">
                {lastRun.enabled
                  ? `${lastRun.candidates} due · ${lastRun.sent} sent${lastRun.failed ? ` · ${lastRun.failed} failed` : ''}`
                  : 'Reminders are disabled — nothing sent.'}
              </span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export const EmailsPage: React.FC = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;

  // History first — the nav bills this tab as the outbound log; the template
  // debug console is the secondary, pre-launch tool.
  const [view, setView] = useState<'history' | 'templates' | 'reminders'>('history');
  const [templatesState, setTemplatesState] = useState<TemplatesState>({ kind: 'loading' });
  const [selectedKey, setSelectedKey] = useState<EmailTemplateKey | null>(null);
  const [selectedLocale, setSelectedLocale] = useState<EmailLocale>('en');
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [recipient, setRecipient] = useState<string>('');
  const [sendStatus, setSendStatus] = useState<SendStatus>({ kind: 'idle' });

  const previewReqId = useRef(0);

  const activeTemplate: EmailTemplateDescriptor | null = useMemo(() => {
    if (templatesState.kind !== 'ready' || !selectedKey) return null;
    return templatesState.templates.find((t) => t.key === selectedKey) ?? null;
  }, [templatesState, selectedKey]);

  useEffect(() => {
    if (!isAdmin) {
      setTemplatesState({ kind: 'forbidden' });
      return;
    }
    api.setToken(token);
    let cancelled = false;
    (async () => {
      const res = await api.get<EmailTemplateDescriptor[]>('/emails/templates');
      if (cancelled) return;
      if (res.success && res.data) {
        setTemplatesState({ kind: 'ready', templates: res.data });
        setSelectedKey(res.data[0]?.key ?? null);
        return;
      }
      if (res.error?.code === 'FORBIDDEN') {
        setTemplatesState({ kind: 'forbidden' });
        return;
      }
      toastApiError(res, 'Failed to load email templates');
      setTemplatesState({
        kind: 'error',
        message: res.error?.message ?? 'Could not load templates',
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, token]);

  // Reset variables when template changes — seed from sample so the form is
  // never empty.
  useEffect(() => {
    if (!activeTemplate) {
      setVariables({});
      return;
    }
    const seeded: Record<string, string> = {};
    for (const v of activeTemplate.variables) {
      const sample = activeTemplate.sample[v.name];
      seeded[v.name] = sample === undefined || sample === null ? '' : String(sample);
    }
    setVariables(seeded);
    setSendStatus({ kind: 'idle' });
  }, [activeTemplate]);

  const requestPreview = useCallback(async () => {
    if (!selectedKey) return;
    const reqId = ++previewReqId.current;
    setPreviewLoading(true);
    setPreviewError(null);
    const res = await api.post<PreviewResponse>('/emails/preview', {
      template_key: selectedKey,
      locale: selectedLocale,
      variables,
    });
    if (reqId !== previewReqId.current) return;
    setPreviewLoading(false);
    if (res.success && res.data) {
      setPreview(res.data);
      return;
    }
    setPreview(null);
    toastApiError(res, 'Failed to render email preview');
    setPreviewError(res.error?.message ?? 'Could not render preview');
  }, [selectedKey, selectedLocale, variables]);

  // Debounced preview refresh. Runs on mount, when selections change, and
  // whenever variables change.
  useEffect(() => {
    if (!selectedKey) return;
    const timer = window.setTimeout(() => {
      void requestPreview();
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [requestPreview, selectedKey]);

  const handleSendTest = useCallback(async () => {
    if (!selectedKey) return;
    if (!isEmail(recipient)) {
      setSendStatus({ kind: 'failed', message: 'Enter a valid email address' });
      return;
    }
    setSendStatus({ kind: 'sending' });
    const res = await api.post<EmailSendResult>('/emails/send-test', {
      template_key: selectedKey,
      locale: selectedLocale,
      recipient: recipient.trim(),
      variables,
    });
    if (res.success && res.data) {
      if (res.data.status === 'sent') {
        setSendStatus({
          kind: 'sent',
          message: `Sent to ${res.data.recipient}.`,
        });
      } else if (res.data.status === 'skipped') {
        setSendStatus({
          kind: 'sent',
          message:
            'SMTP is not configured — email logged to server console and saved to email_logs (status: skipped).',
        });
      } else {
        setSendStatus({
          kind: 'failed',
          message: res.data.error_message ?? 'Unexpected status',
        });
      }
      return;
    }
    toastApiError(res, 'Failed to send test email');
    setSendStatus({
      kind: 'failed',
      message: res.error?.message ?? 'Could not send test email',
    });
  }, [recipient, selectedKey, selectedLocale, variables]);

  if (templatesState.kind === 'forbidden') {
    return (
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Emails</h2>
        <Forbidden />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium text-gray-900">Emails</h2>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'history'}
              onClick={() => setView('history')}
              className={`px-3 py-1.5 ${
                view === 'history' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              History
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'templates'}
              onClick={() => setView('templates')}
              className={`px-3 py-1.5 border-l border-gray-300 ${
                view === 'templates' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Templates
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'reminders'}
              onClick={() => setView('reminders')}
              className={`px-3 py-1.5 border-l border-gray-300 ${
                view === 'reminders' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Reminders
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          {view === 'history'
            ? 'Every transactional email the platform has sent or attempted'
            : view === 'templates'
              ? 'Debug console — preview and send transactional emails'
              : 'Automatic nudges for users with documents still waiting to be uploaded'}
        </p>
      </div>

      {view === 'history' && <EmailHistory token={token} />}

      {view === 'reminders' && <RemindersPanel token={token} />}

      {view === 'templates' && templatesState.kind === 'error' && (
        <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {templatesState.message}
        </div>
      )}

      {view === 'templates' && templatesState.kind === 'loading' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" aria-busy="true">
          <div className="lg:col-span-2 space-y-3">
            <span className="block h-10 w-full shimmer" />
            <span className="block h-28 w-full shimmer" />
            <span className="block h-40 w-full shimmer" />
          </div>
          <div className="lg:col-span-3">
            <span className="block h-[480px] w-full shimmer" />
          </div>
        </div>
      )}

      {view === 'templates' && templatesState.kind === 'ready' && activeTemplate && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">
                    Template
                  </label>
                  <select
                    value={selectedKey ?? ''}
                    onChange={(e) => setSelectedKey(e.target.value as EmailTemplateKey)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                  >
                    {templatesState.templates.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{activeTemplate.description}</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-1">
                    Language
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {EMAIL_LOCALES.map((locale) => {
                      const active = locale === selectedLocale;
                      return (
                        <button
                          key={locale}
                          type="button"
                          onClick={() => setSelectedLocale(locale)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                            active
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {EMAIL_LOCALE_LABELS[locale]}
                          {EMAIL_LOCALE_DIRECTIONS[locale] === 'rtl' && (
                            <span className="ml-1 text-[10px] opacity-70">RTL</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Variables</h3>
                <button
                  type="button"
                  onClick={() => {
                    const seeded: Record<string, string> = {};
                    for (const v of activeTemplate.variables) {
                      const sample = activeTemplate.sample[v.name];
                      seeded[v.name] = sample === undefined || sample === null ? '' : String(sample);
                    }
                    setVariables(seeded);
                  }}
                  className="text-xs text-gray-600 underline"
                >
                  Reset to sample
                </button>
              </div>
              <div className="space-y-3">
                {activeTemplate.variables.map((v) => (
                  <div key={v.name}>
                    <label className="block text-xs text-gray-600 mb-1" htmlFor={`var-${v.name}`}>
                      {v.label}{' '}
                      <span className="text-gray-400 font-mono">{`{${v.name}}`}</span>
                    </label>
                    {v.multiline ? (
                      <textarea
                        id={`var-${v.name}`}
                        value={variables[v.name] ?? ''}
                        onChange={(e) =>
                          setVariables((prev) => ({ ...prev, [v.name]: e.target.value }))
                        }
                        rows={3}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                      />
                    ) : (
                      <input
                        id={`var-${v.name}`}
                        type="text"
                        value={variables[v.name] ?? ''}
                        onChange={(e) =>
                          setVariables((prev) => ({ ...prev, [v.name]: e.target.value }))
                        }
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Send a test email</h3>
              <p className="text-xs text-gray-500 mb-3">
                Delivers the current preview to any address using the configured SMTP transport. If
                SMTP is not configured the send is logged with status <code>skipped</code>.
              </p>
              <div className="space-y-2">
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                />
                <button
                  type="button"
                  onClick={() => void handleSendTest()}
                  disabled={sendStatus.kind === 'sending'}
                  className="w-full px-3 py-2 rounded-md text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50"
                >
                  {sendStatus.kind === 'sending' ? 'Sending…' : 'Send test email'}
                </button>
              </div>
              {sendStatus.kind === 'sent' && (
                <p className="mt-2 text-xs text-green-700" role="status">
                  {sendStatus.message}
                </p>
              )}
              {sendStatus.kind === 'failed' && (
                <p className="mt-2 text-xs text-red-700" role="alert">
                  {sendStatus.message}
                </p>
              )}
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card padding="none">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-md">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Subject</p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {preview?.subject ?? '—'}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {previewLoading ? 'Rendering…' : `${selectedLocale.toUpperCase()}`}
                </div>
              </div>
              {previewError && (
                <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-b border-red-200">
                  {previewError}
                </div>
              )}
              {preview?.preheader && (
                <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-200 italic">
                  Preheader: {preview.preheader}
                </div>
              )}
              <div className="bg-paper-2">
                <iframe
                  title="Email preview"
                  srcDoc={preview?.html ?? ''}
                  sandbox="allow-same-origin"
                  className="w-full border-0 rounded-b-md"
                  style={{ height: '1400px', display: 'block' }}
                />
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
